import React, { forwardRef } from 'react'
import { Check } from 'phosphor-react'

export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> {
  label?: string
  error?: string
  helperText?: string
  size?: 'sm' | 'md' | 'lg'
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      error,
      helperText,
      size = 'md',
      className = '',
      disabled,
      checked,
      onChange,
      id,
      ...props
    },
    ref
  ) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`

    const sizeStyles = {
      sm: {
        checkbox: 'w-4 h-4',
        icon: 'w-2.5 h-2.5',
        label: 'text-sm',
      },
      md: {
        checkbox: 'w-5 h-5',
        icon: 'w-3 h-3',
        label: 'text-sm',
      },
      lg: {
        checkbox: 'w-6 h-6',
        icon: 'w-4 h-4',
        label: 'text-base',
      },
    }

    const currentSize = sizeStyles[size]

    return (
      <div className={`flex flex-col ${className}`}>
        <label
          htmlFor={checkboxId}
          className={`
            flex items-start gap-2.5 
            ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <div className="relative flex-shrink-0">
            <input
              ref={ref}
              type="checkbox"
              id={checkboxId}
              checked={checked}
              onChange={onChange}
              disabled={disabled}
              className="sr-only peer"
              {...props}
            />
            <div
              className={`
                ${currentSize.checkbox}
                relative
                border-2 rounded transition-all duration-200
                flex items-center justify-center
                peer-focus:outline-none
                peer-disabled:cursor-not-allowed peer-disabled:opacity-50
                ${error ? 'border-[var(--color-error)]' : 'border-[var(--color-border)]'}
                ${
                  checked
                    ? error
                      ? 'bg-[var(--color-error)] border-[var(--color-error)]'
                      : 'bg-[var(--color-primary)] border-[var(--color-primary)]'
                    : 'bg-white hover:border-[var(--color-primary)]'
                }
                ${disabled ? 'opacity-50' : ''}
              `}
            >
              {checked && (
                <Check
                  className={`${currentSize.icon} text-white transition-all duration-200 ${
                    checked ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                  }`}
                  weight="bold"
                />
              )}
            </div>
          </div>
          {label && (
            <span
              className={`
                ${currentSize.label}
                font-medium
                select-none
                transition-colors duration-200
                flex-1
                ${disabled ? 'opacity-50' : ''}
                ${error ? 'text-[var(--color-error)]' : 'text-[var(--color-text-secondary)]'}
              `}
            >
              {label}
            </span>
          )}
        </label>
        {error && (
          <p className="mt-1.5 text-sm text-[var(--color-error)]" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="mt-1.5 text-sm text-[var(--color-text-muted)]">{helperText}</p>
        )}
      </div>
    )
  }
)

Checkbox.displayName = 'Checkbox'
